import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getPageNumbers } from "../../utils/getPageNumbers";
import "./styles.scss";

export interface AccessTablePaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    disabled?: boolean;
    hasNextPage?: boolean;
    hasPreviousPage?: boolean;
    ariaLabel?: string;
}

const AccessTablePagination: React.FC<AccessTablePaginationProps> = ({
    currentPage,
    totalPages,
    onPageChange,
    disabled = false,
    hasNextPage,
    hasPreviousPage,
    ariaLabel = "Paginação",
}) => {
    if (totalPages <= 1) {
        return null;
    }

    const canGoPrevious = hasPreviousPage ?? currentPage > 1;
    const canGoNext = hasNextPage ?? currentPage < totalPages;

    return (
        <nav className="access-pagination" aria-label={ariaLabel}>
            <div className="access-pagination__controls">
                <button
                    type="button"
                    className="access-pagination__nav-button"
                    aria-label="Página anterior"
                    onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                    disabled={!canGoPrevious || disabled}
                >
                    <ChevronLeft size={16} />
                </button>

                <div className="access-pagination__pages">
                    {getPageNumbers(currentPage, totalPages).map((page, index) =>
                        page === "..." ? (
                            <span
                                key={`ellipsis-${index}`}
                                className="access-pagination__ellipsis"
                                aria-hidden="true"
                            >
                                ...
                            </span>
                        ) : (
                            <button
                                key={page}
                                type="button"
                                className={`access-pagination__page ${currentPage === page ? "access-pagination__page--active" : ""}`}
                                aria-label={`Ir para página ${page}`}
                                aria-current={currentPage === page ? "page" : undefined}
                                onClick={() => onPageChange(page)}
                                disabled={disabled}
                            >
                                {page}
                            </button>
                        )
                    )}
                </div>

                <button
                    type="button"
                    className="access-pagination__nav-button"
                    aria-label="Próxima página"
                    onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={!canGoNext || disabled}
                >
                    <ChevronRight size={16} />
                </button>
            </div>
        </nav>
    );
};

export default AccessTablePagination;
